import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Packages
import { Button, Popconfirm, Space, Spin, Table, Tooltip, Typography } from 'antd';
import { QuestionCircleFilled } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

// Actions
import { get_all_self_errors, self_error_delete } from '../../../redux/actions/selfErrorsActions';

const ErrorsTable = (props) => {
	const { messageApi, orgFilters, setIsModalOpen, setUpdate, setErrorToUpdate, pageSize, skip, setSkip } = props;

	const { t } = useTranslation();

	const dispatch = useDispatch();

	const _ls = { ...localStorage };

	const { all_self_errors_loading, all_self_errors, self_error_delete_loading } = useSelector(state => state.self_errors);

	const update = (record) => {
		setIsModalOpen(true);
		setUpdate(true);
		setErrorToUpdate(record);
	}

	const deleteSelfError = (record) => {
		const filters = { ...orgFilters, skip: skip, limit: pageSize };
		dispatch(self_error_delete(messageApi, record._id.$oid, filters));
	}

	const columns = [
		...(orgFilters.organization === ''
			? [
					{
						title: t('filters.organization'),
						dataIndex: ['organization', '$oid'],
						key: 'organization'
					}
				]
			: []
		),
		{
			title: t('errorForm.inputs.alias'),
			dataIndex: 'alias',
			key: 'alias',
			render: (_, record) => (
				<Typography.Text copyable={{ text: record._id.$oid, tooltips: [t('copyableText.copyId'), t('copyableText.copiedId')] }}>
					{record.alias}
				</Typography.Text>
			)
		},
		{
			title: t('routesTable.headers.name'),
			dataIndex: 'name',
			key: 'name'
		},
		{
			title: t('loadFileInput.description'),
			dataIndex: 'description',
			key: 'description'
		},
		{
			title: t('errorForm.inputs.value'),
			dataIndex: 'value',
			key: 'value'
		},
		{
			title: t('loadStatus.statusTable.date'),
			dataIndex: ['date', '$date'],
			key: 'date',
			render: (text) => (
				<Typography.Text>{new Date(text).toLocaleString(_ls.i18nextLng, { hour12: true })}</Typography.Text>
			)
		},
		{
			title: t('routesTable.headers.action'),
			dataIndex: 'action',
			key: 'action',
			render: (_, record) => (
				<Space>
					<Tooltip title={t('errorForm.edit.title').split(' ')[0]}>
						<Button
							type='primary'
							shape='circle'
							size='large'
							icon={<i className='bi bi-pencil-fill'></i>}
							onClick={() => update(record)}
						/>
					</Tooltip>
					<Popconfirm
						title={t('errorForm.delete.popconfirm.title')}
						description={t('errorForm.delete.popconfirm.description')}
						icon={<QuestionCircleFilled style={{ color: '#1677FF' }} />}
						onConfirm={() => deleteSelfError(record)}
						okButtonProps={{
							loading: self_error_delete_loading
						}}
						okText={t('errorForm.delete.popconfirm.confirm')}
						cancelText={t('errorForm.delete.popconfirm.cancel')}
					>
						<Button
							type='primary'
							shape='circle'
							size='large'
							danger
							icon={<span className='material-symbols-outlined'>delete</span>}
						/>
					</Popconfirm>
				</Space>
			)
		}
	];

	const handleTableChange = (pagination, filters, sorter) => {
		const skip = (pagination.current - 1) * pageSize;
		setSkip(skip);

		const errorsFilters = { ...orgFilters, skip: skip, limit: pageSize };
		dispatch(get_all_self_errors(errorsFilters));
	};

	return (
		<div className='mt-5'>
			<Spin spinning={all_self_errors_loading} tip={t('errors.loading')} size='large'>
				<Table
					columns={columns}
					dataSource={all_self_errors.errors}
					onChange={handleTableChange}
					pagination={{
						pageSize: pageSize,
						total: all_self_errors.count,
						showTotal: (total, range) => `${range[0]} - ${range[1]} ${t('pagination.of')} ${total}`
					}}
					scroll={{
						x: 1000
					}}
				/>
			</Spin>
		</div>
	)
}

ErrorsTable.propTypes = {
	messageApi: PropTypes.object.isRequired,
	orgFilters: PropTypes.object.isRequired,
	setIsModalOpen: PropTypes.func.isRequired,
	setUpdate: PropTypes.func.isRequired,
	setErrorToUpdate: PropTypes.func.isRequired,
	pageSize: PropTypes.number.isRequired,
	skip: PropTypes.number.isRequired,
	setSkip: PropTypes.func.isRequired
}

export default ErrorsTable;
